import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Select, Row, Col, Card, Table, Segmented, Dropdown, Modal, ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import { HappyProvider } from "@ant-design/happy-work-theme";
import { SearchOutlined } from "@ant-design/icons";
import { DataBadge, AppPagination } from "../../components/ui";

import { actionDesvinculacionesCards } from "../../redux/actions/desvinculaciones/desvinculaciones.js";
import { actionEmpresasdistinct } from "../../redux/actions/empresas/empresas.js";
import { PATH } from "../../redux/utils.js";
import { laboralTiposConfig, PAGE_SIZE } from "./materias/laboral/laboralProcedimiento.config.js";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos.js";
import useExpedientesProcedimiento from "./materias/laboral/useDesvinculacionesProcedimientos.js";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import { previewDesvinculacionesReportPdf, printDesvinculacionesReportPdf } from "../../components/utils/printDesvinculacionesReportPdf.js";
import ImportarExcelModal from "./utils/ImportarExcelModal.jsx";

import "./materias/laboral/LaboralProcedimientoPage.css";
import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";

dayjs.locale("es");

const apiGet = axios.create({ baseURL: PATH, headers: { "Content-Type": "application/json" } });
const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ALL = "__all__";

const MODALIDAD_FILTER_OPTIONS = [
  { label: "Todas las modalidades", value: null },
  { label: "Privada", value: "privado" },
  { label: "Ratificada", value: "ratificado" },
];

const RESULTADO_FILTER_OPTIONS = [
  { label: "Todos los resultados", value: null },
  { label: "Solucionada con éxito", value: "1" },
  { label: "En espera de citatorio prejudicial", value: "2" },
];

/* ─── helpers ───────────────────────────────────────────────────────── */
const collapseSpaces = (str) => String(str || "").replace(/\s+/g, " ").trim();

const normalizeStr = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

const formatDateEs = (d) => {
  if (!d) return null;
  const x = dayjs(d);
  return x.isValid() ? x.format("DD MMM YYYY") : null;
};

const computeEstatus = (exp) => {
  const idEstado = exp?.id_desvinculacion_estado ? Number(exp.id_desvinculacion_estado) : null;
  const idResultado = exp?.id_desvinculacion_resultado ? Number(exp.id_desvinculacion_resultado) : null;

  // Resultado tiene prioridad
  if (idResultado === 1) return { label: "SOLUCIONADA CON ÉXITO", kind: "green" };
  if (idResultado === 2) return { label: "EN ESPERA DE CITATORIO", kind: "red" };

  // Estado
  if (idEstado === 2) return { label: "EN PROCESO", kind: "yellow" };
  if (idEstado === 3) return { label: "CANCELADA", kind: "gray" };
  if (idEstado === 1) return { label: "REGISTRADA", kind: "gray" };

  // Fallback string para registros legacy
  const raw = normalizeStr(exp?.status_nombre || exp?.status || exp?.estatus || "");
  if (raw.includes("solucion") || raw.includes("exito"))
    return { label: "SOLUCIONADA CON ÉXITO", kind: "green" };
  if (raw.includes("proceso"))
    return { label: "EN PROCESO", kind: "yellow" };
  if (raw.includes("citatorio") || raw.includes("prejudicial") || raw.includes("espera"))
    return { label: "EN ESPERA DE CITATORIO", kind: "red" };
  return { label: "REGISTRADA", kind: "gray" };
};

const MODALIDAD_LABEL = { privado: "Privada", ratificado: "Ratificada" };
const LUGAR_LABEL = {
  centro_trabajo: "Centro de trabajo",
  despacho: "Despacho",
  centro_conciliacion: "Centro de Conciliación",
};

const mapRow = (exp, modalidadMap = {}, lugarMap = {}) => {
  const id = exp?.id_desvinculacion ?? exp?.id;
  const actor = collapseSpaces(
    exp?.nombre_solicitante || exp?.parte_actora || exp?.trabajador || exp?.nombre_trabajador || ""
  );
  const contra = collapseSpaces(
    exp?.empresa_nombre || exp?.nombre_empresa || exp?.razon_social ||
    exp?.empresa || exp?.parte_demandada || ""
  );

  const caseTitle = [actor || "—", contra].filter(Boolean).join(" — ");
  const estatus = computeEstatus(exp);
  const fechaBaja = exp?.fecha_baja || exp?.fecha_ejecucion || exp?.fecha_solicitacion || null;
  const fechaSolicitud = exp?.fecha_solicitud || exp?.fecha_encargo || null;

  // Modalidad de baja
  const viaTramite = exp?.via_tramite || exp?.modalidad_baja || null;
  const modalidad =
    exp?.nombre_modalidad_baja ||
    (exp?.id_modalidad_baja ? modalidadMap[exp.id_modalidad_baja] : null) ||
    (viaTramite ? (MODALIDAD_LABEL[viaTramite] || viaTramite) : null);

  // Lugar de ejecución
  const lugarRaw = exp?.lugar_ejecucion || null;
  const origenPrivado = exp?.origen_privado || null;
  const lugar =
    exp?.nombre_lugar_ejecucion ||
    (exp?.id_lugar_ejecucion ? lugarMap[exp.id_lugar_ejecucion] : null) ||
    (origenPrivado ? (LUGAR_LABEL[origenPrivado] || origenPrivado) : null) ||
    (lugarRaw ? (LUGAR_LABEL[lugarRaw] || lugarRaw) : null);

  // Domicilio de ejecución
  const domicilio = exp?.domicilio || exp?.domicilio_ejecucion || null;

  // Número de empleados
  const numEmpleados = Array.isArray(exp?.empleados) ? exp.empleados.length : (exp?.num_empleados ?? null);

  // Only show folio/expediente if it's a real number, not just the raw DB id
  const folioReal = exp?.numero_expediente || exp?.folio || null;

  // Vencido: la fecha de baja ya pasó y el expediente sigue abierto (registrada / en proceso)
  const fechaBajaValida = fechaBaja ? dayjs(fechaBaja) : null;
  const vencido = !!(
    (estatus.kind === "gray" || estatus.kind === "yellow") &&
    fechaBajaValida && fechaBajaValida.isValid() && fechaBajaValida.isBefore(dayjs())
  );

  return {
    id: Number.isFinite(Number(id)) ? Number(id) : null,
    folioReal,
    actor: actor || "—",
    contraparte: contra || "—",
    caseTitle,
    estatus,
    fechaBaja,
    fechaSolicitud,
    modalidad,
    lugar,
    domicilio,
    numEmpleados,
    vencido,
  };
};

/* ─── sub-components (mismo patrón que Tribunal) ─────────────────────── */
const KPI_BG_VARIANT = { blue: "info", green: "success", gray: "secondary", yellow: "warning", red: "danger" };

const KPI_ICON_COLORS = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  success: { bg: "#16A34A26", fg: "#16A34A" },
  secondary: { bg: "#F3F4F6", fg: "#6B7280" },
  warning: { bg: "#FEF3C7", fg: "#F59E0B" },
  danger: { bg: "#FEE2E2", fg: "#DC2626" },
};

function KpiCardDesv({ icon, iconBg, value, label, active = false, onClick }) {
  const variant = KPI_BG_VARIANT[iconBg] || "secondary";
  const iconColors = KPI_ICON_COLORS[variant] || KPI_ICON_COLORS.secondary;
  return (
    <Card
      role={onClick ? "button" : undefined}
      onClick={onClick}
      bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderColor: active ? "#0A539A" : undefined,
        borderWidth: active ? 2 : undefined,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 44, height: 44, background: iconColors.bg, color: iconColors.fg, fontSize: 18, flexShrink: 0 }}
      >
        <i className={`fa-solid ${icon}`} />
      </span>
      <div>
        <div className="fs-4 fw-bold lh-1">{value}</div>
        <div className="small text-secondary">{label}</div>
      </div>
    </Card>
  );
}

function StatusPillDesv({ kind, label }) {
  return (
    <div style={{ width: 168, flexShrink: 0 }}>
      <DataBadge
        color={STATUS_PILL_HEX[kind] || kind}
        className="d-flex align-items-center justify-content-center gap-1 status-pill-badge"
        style={{ whiteSpace: "normal", width: "100%", boxSizing: "border-box", lineHeight: 1.3, textAlign: "center" }}
      >
        {label}
      </DataBadge>
    </div>
  );
}

function FooterPagerDesv({ total, totalPages, page, setPage }) {
  const ps = PAGE_SIZE || 20;
  const start = total === 0 ? 0 : (page - 1) * ps + 1;
  const end = Math.min(page * ps, total);
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <small className="text-secondary">
        Mostrando <strong>{start}</strong> a <strong>{end}</strong> de <strong>{total}</strong> resultados
      </small>
      <AppPagination current={page} total={total} pageSize={ps} onChange={setPage} size="sm" />
    </div>
  );
}

// ícono "info" (fa-circle-info) reutilizado en los botones "Ver detalles"
const VER_DETALLES_ICON =
  "M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z";

// ícono de calendario (fa-calendar-day) para la fecha de baja destacada
const CALENDAR_ICON =
  "M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z";

function TableView({ pageRows, total, totalPages, page, setPage, onNavigate }) {
  const columns = [
    {
      title: "Trabajador / Empresa",
      key: "trabajador",
      width: 280,
      render: (_, r) => (
        <>
          {r.folioReal && <div className="small text-secondary">Folio: {r.folioReal}</div>}
          <div className="fw-semibold text-uppercase">{r.caseTitle}</div>
        </>
      ),
    },
    {
      title: "Estatus",
      key: "estatus",
      render: (_, r) => <StatusPillDesv kind={r.estatus.kind} label={r.estatus.label} />,
    },
    {
      title: "Modalidad / Lugar",
      key: "modalidad",
      render: (_, r) => (
        <>
          <div className="small">{r.modalidad || "—"}</div>
          <div className="small text-secondary">{r.lugar || "—"}</div>
        </>
      ),
    },
    {
      title: "Fecha de baja",
      key: "fechaBaja",
      render: (_, r) => (
        <div className={`exp-audiencia-highlight exp-audiencia-highlight--warning${r.vencido ? "" : ""}`}>
          <div className="exp-audiencia-highlight-icon-wrap">
            <svg className="exp-audiencia-highlight-icon" viewBox="0 0 448 512" width="17" height="17" fill="currentColor" aria-hidden="true">
              <path d={CALENDAR_ICON} />
            </svg>
          </div>
          <div className="exp-audiencia-highlight-body">
            <div className="exp-audiencia-highlight-label">Fecha de baja</div>
            <div className="exp-audiencia-highlight-value">{formatDateEs(r.fechaBaja) || "N/A"}</div>
            {r.vencido && <div className="exp-audiencia-highlight-sub">Vencido</div>}
          </div>
        </div>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "right",
      render: (_, r) => (
        <Button type="primary" block onClick={() => onNavigate(r.id)}>
          <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
            <path d={VER_DETALLES_ICON} />
          </svg>
          Ver detalles
        </Button>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Table columns={columns} dataSource={pageRows} rowKey={(r) => r.id} pagination={false} />
      <div className="p-3" style={{ borderTop: "1px solid #eef2f7", background: "#fff" }}>
        <FooterPagerDesv total={total} totalPages={totalPages} page={page} setPage={setPage} />
      </div>
    </Card>
  );
}

function CardsView({ rows, total, totalPages, page, setPage, onNavigate }) {
  return (
    <>
      <Row gutter={[24, 24]}>
        {rows.map((r) => (
          <Col key={r.id} xs={24} md={12} xl={8} xxl={6}>
            <Card className="h-100 exp-card" bodyStyle={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="fw-semibold text-uppercase">{r.caseTitle}</div>
                <StatusPillDesv kind={r.estatus.kind} label={r.estatus.label} />
              </div>

              <hr className="my-1" />

              <div className="d-flex flex-column gap-1 small text-secondary">
                <div>
                  <i className="fa-solid fa-right-left me-1" />
                  Modalidad: <strong>{r.modalidad || "—"}</strong>
                </div>
                <div>
                  <i className="fa-solid fa-location-dot me-1" />
                  Lugar de ejecución: <strong>{r.lugar || "—"}</strong>
                </div>
                {r.domicilio && (
                  <div>
                    <i className="fa-solid fa-house me-1" />
                    Domicilio: <strong>{r.domicilio}</strong>
                  </div>
                )}
                {r.numEmpleados != null && (
                  <div>
                    <i className="fa-solid fa-users me-1" />
                    Trabajador{r.numEmpleados !== 1 ? "es" : ""}: <strong>{r.numEmpleados}</strong>
                  </div>
                )}
                {r.folioReal && (
                  <div>
                    <i className="fa-solid fa-hashtag me-1" />
                    Folio: <strong>{r.folioReal}</strong>
                  </div>
                )}
              </div>

              <div className="mt-auto">
                <div className="exp-audiencia-highlight exp-audiencia-highlight--warning mb-2">
                  <div className="exp-audiencia-highlight-icon-wrap">
                    <svg className="exp-audiencia-highlight-icon" viewBox="0 0 448 512" width="17" height="17" fill="currentColor" aria-hidden="true">
                      <path d={CALENDAR_ICON} />
                    </svg>
                  </div>
                  <div className="exp-audiencia-highlight-body">
                    <div className="exp-audiencia-highlight-label">Fecha de baja</div>
                    <div className="exp-audiencia-highlight-value">{formatDateEs(r.fechaBaja) || "N/A"}</div>
                    {r.vencido && <div className="exp-audiencia-highlight-sub">Vencido: la fecha de baja ya pasó</div>}
                  </div>
                </div>

                <Button type="primary" block onClick={() => onNavigate(r.id)}>
                  <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    <path d={VER_DETALLES_ICON} />
                  </svg>
                  Ver detalles
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="mt-3">
        <FooterPagerDesv total={total} totalPages={totalPages} page={page} setPage={setPage} />
      </div>
    </>
  );
}

const EXPORT_MENU_TONES = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  green: { bg: "#DCFCE7", fg: "#16A34A" },
};

function ExportMenuRow({ viewBox, path, tone, title, subtitle }) {
  const colors = EXPORT_MENU_TONES[tone] || EXPORT_MENU_TONES.primary;
  const [, , vbW, vbH] = viewBox.split(" ").map(Number);
  const height = 16;
  const width = Math.round((vbW / vbH) * height);
  return (
    <div className="laboral-export-menu-row">
      <span className="laboral-export-menu-icon" style={{ background: colors.bg, color: colors.fg }}>
        <svg viewBox={viewBox} width={width} height={height} fill="currentColor" aria-hidden="true">
          <path d={path} />
        </svg>
      </span>
      <div className="laboral-export-menu-text">
        <div className="laboral-export-menu-title">{title}</div>
        <div className="laboral-export-menu-subtitle">{subtitle}</div>
      </div>
    </div>
  );
}

/* ─── main page ──────────────────────────────────────────────────────── */
export default function LaboralDesvinculacionesPage() {
  const { tipo } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreviewHtml, setExportPreviewHtml] = useState("");

  const toIntOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const n = Number(String(v).trim());
    return Number.isFinite(n) ? n : null;
  };

  const setOrDelete = (sp, key, value) => {
    if (value === null || value === undefined || value === "" || value === "todos") {
      sp.delete(key);
      return;
    }
    sp.set(key, String(value));
  };

  const tipoConfig = laboralTiposConfig[tipo] || laboralTiposConfig["desvinculacion"];
  const concSlice = useSelector((state) => state.desvinculaciones || {});

  /* ── catálogos para lookup de IDs ── */
  const [modalidadMap, setModalidadMap] = useState({});
  const [lugarMap, setLugarMap] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [mRes, lRes] = await Promise.all([
          apiGet.get("catalogos/modalidad_baja", { headers: authHeader() }),
          apiGet.get("catalogos/lugar_ejecucion", { headers: authHeader() }),
        ]);
        const mData = Array.isArray(mRes.data) ? mRes.data : (mRes.data?.data ?? []);
        const lData = Array.isArray(lRes.data) ? lRes.data : (lRes.data?.data ?? []);
        const mm = {};
        mData.forEach((d) => { if (d.id_modalidad_baja != null) mm[d.id_modalidad_baja] = d.nombre; });
        const lm = {};
        lData.forEach((d) => { if (d.id_lugar_ejecucion != null) lm[d.id_lugar_ejecucion] = d.nombre; });
        setModalidadMap(mm);
        setLugarMap(lm);
      } catch (e) {
        console.error("Error cargando catálogos desvinculaciones:", e);
      }
    };
    load();
  }, []);

  /* ── state ── */
  const [selectedEstadoId, setSelectedEstadoId] = useState(ALL);
  const [selectedCiudadId, setSelectedCiudadId] = useState(ALL);
  const [empresaIdMap, setEmpresaIdMap] = useState({});
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [empresasOptionsDistinct, setEmpresasOptionsDistinct] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [empresaFilter, setEmpresaFilter] = useState(null);
  const [modalidadFilter, setModalidadFilter] = useState(null);
  const [resultadoFilter, setResultadoFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState("cards");
  const [sortOption, setSortOption] = useState("default");

  /* ── catálogos ── */
  const estadoIdForCatalogos = selectedEstadoId === ALL ? null : selectedEstadoId;
  const ciudadIdForCatalogos = selectedCiudadId === ALL ? null : selectedCiudadId;

  const {
    estadosOptions,
    ciudadesOptions,
    empresasOptions,
    estadoSeleccionado,
    ciudadSeleccionada,
    estadosById,
    ciudadesById,
  } = useLaboralCatalogos(estadoIdForCatalogos, ciudadIdForCatalogos, { modulo: "desvinculaciones" });

  const estadosOptionsFinal = useMemo(
    () => [{ label: "Todos los estados", value: ALL }, ...(estadosOptions || [])],
    [estadosOptions]
  );

  const ciudadesOptionsFinal = useMemo(() => {
    const base = selectedEstadoId === ALL ? [] : (ciudadesOptions || []);
    return [{ label: "Todas las ciudades", value: ALL }, ...base];
  }, [ciudadesOptions, selectedEstadoId]);


  // Restore from URL on mount
  useEffect(() => {
    const estado = toIntOrNull(searchParams.get("estado"));
    const ciudad = toIntOrNull(searchParams.get("ciudad"));
    const empresa = searchParams.get("empresa");
    const q = searchParams.get("q") || "";
    const status = searchParams.get("status") || "todos";
    const page = toIntOrNull(searchParams.get("page")) || 1;
    const modalidad = searchParams.get("modalidad") || null;
    const resultado = searchParams.get("resultado") || null;

    if (estado) setSelectedEstadoId(estado);
    if (ciudad) setSelectedCiudadId(ciudad);
    if (empresa) setEmpresaFilter(empresa);
    if (q) setSearch(q);
    if (status) setStatusFilter(status);
    if (modalidad) setModalidadFilter(modalidad);
    if (resultado) setResultadoFilter(resultado);
    setCurrentPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Garantiza que estado sea ALL si llega null (ej. navegación back)
  useEffect(() => {
    if (selectedEstadoId == null) setSelectedEstadoId(ALL);
  }, [selectedEstadoId]);

  // Sync state → URL
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    setOrDelete(sp, "estado", selectedEstadoId);
    setOrDelete(sp, "ciudad", selectedCiudadId);
    setOrDelete(sp, "empresa", empresaFilter);
    setOrDelete(sp, "q", collapseSpaces(search));
    setOrDelete(sp, "status", statusFilter);
    setOrDelete(sp, "modalidad", modalidadFilter);
    setOrDelete(sp, "resultado", resultadoFilter);
    setOrDelete(sp, "page", currentPage);
    const next = sp.toString();
    const prev = searchParams.toString();
    if (next !== prev) setSearchParams(sp, { replace: true });
  }, [selectedEstadoId, selectedCiudadId, empresaFilter, search, statusFilter, modalidadFilter, resultadoFilter, currentPage, searchParams, setSearchParams]);

  const handleChangeEstado = (value) => {
    setSelectedEstadoId(value ?? ALL);
    setSelectedCiudadId(ALL);
    setEmpresaFilter(null);
    setCurrentPage(1);
  };

  const handleChangeCiudad = (value) => {
    setSelectedCiudadId(value ?? ALL);
    setEmpresaFilter(null);
    setCurrentPage(1);
  };

  /* ── backend fetch ── */
  const filtrosBusqueda = useMemo(() => ({
    tipo_desvinculacion: tipoConfig.key,
    ...(selectedEstadoId !== ALL ? { id_estado: selectedEstadoId } : {}),
    ...(selectedCiudadId !== ALL ? { id_ciudad: selectedCiudadId } : {}),
    ...(selectedEmpresaId ? { id_empresa: selectedEmpresaId } : {}),
    ...(collapseSpaces(search) ? { nombre_solicitante: collapseSpaces(search) } : {}),
  }), [selectedEstadoId, selectedCiudadId, tipoConfig.key, selectedEmpresaId, search]);

  useEffect(() => {
    dispatch(actionDesvinculacionesCards(filtrosBusqueda));
  }, [dispatch, filtrosBusqueda]);

  /* ── distinct empresas ── */
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (selectedEstadoId === ALL || selectedCiudadId === ALL) {
        if (isMounted) { setEmpresasOptionsDistinct([]); setEmpresaIdMap({}); }
        return;
      }
      const resp = await dispatch(
        actionEmpresasdistinct({
          estado: selectedEstadoId,
          ciudad: selectedCiudadId,
          tipo_desvinculacion: tipoConfig.key,
        })
      );
      if (!isMounted) return;
      if (typeof resp === "string") { setEmpresasOptionsDistinct([]); setEmpresaIdMap({}); return; }
      const arr = Array.isArray(resp) ? resp : resp?.items || resp?.data || [];
      const map = {};
      arr.forEach((name) => {
        const clean = collapseSpaces(name);
        const match = empresasOptions.find((e) => collapseSpaces(e.label) === clean);
        if (match) map[clean] = match.value;
      });
      setEmpresaIdMap(map);
      const options = Array.isArray(arr)
        ? arr.map((x) => {
            const label = (typeof x === "string" ? x : x?.razon_social || x?.nombre) || "";
            const clean = collapseSpaces(label);
            if (!clean) return null;
            return { label: clean, value: clean };
          }).filter(Boolean)
        : [];
      setEmpresasOptionsDistinct(options);
    };
    load();
    return () => { isMounted = false; };
  }, [dispatch, selectedEstadoId, selectedCiudadId, tipoConfig.key]);

  const empresasFinalOptions = useMemo(() => {
    const base = empresasOptionsDistinct.length > 0 ? empresasOptionsDistinct : empresasOptions;
    return [{ label: "Todas las empresas", value: null }, ...(base || [])];
  }, [empresasOptionsDistinct, empresasOptions]);

  useEffect(() => {
    if (!empresaFilter) { setSelectedEmpresaId(null); return; }
    const clean = collapseSpaces(empresaFilter);
    setSelectedEmpresaId(empresaIdMap?.[clean] ?? null);
  }, [empresaFilter, empresaIdMap]);

  /* ── local filtering / pagination ── */
  const {
    statusCounts,
    filteredExpedientes,
    paginatedExpedientes,
  } = useExpedientesProcedimiento({
    concSlice,
    search,
    statusFilter,
    fueroFilter: "todos",
    empresaId: selectedEmpresaId,
    dateRange: null,
    currentPage,
    pageSize: PAGE_SIZE,
    setCurrentPage,
  });

  const extraFiltered = useMemo(() => {
    let arr = Array.isArray(filteredExpedientes) ? filteredExpedientes : [];
    if (modalidadFilter) {
      arr = arr.filter((exp) => {
        const v = normalizeStr(exp?.via_tramite || exp?.modalidad_baja || exp?.nombre_modalidad_baja || "");
        return v === normalizeStr(modalidadFilter) || v.includes(normalizeStr(modalidadFilter));
      });
    }
    if (resultadoFilter) {
      arr = arr.filter((exp) => {
        const v = exp?.id_desvinculacion_resultado;
        return v != null && String(v) === String(resultadoFilter);
      });
    }
    return arr;
  }, [filteredExpedientes, modalidadFilter, resultadoFilter]);

  const sortedFiltered = useMemo(() => {
    const list = [...extraFiltered];
    if (sortOption === "default") return list;

    const getCaseTitleSortKey = (exp) => {
      const actor = collapseSpaces(
        exp?.nombre_solicitante || exp?.parte_actora || exp?.trabajador || exp?.nombre_trabajador || ""
      );
      const contra = collapseSpaces(
        exp?.empresa_nombre || exp?.nombre_empresa || exp?.razon_social ||
        exp?.empresa || exp?.parte_demandada || ""
      );
      return `${actor} — ${contra}`.toLowerCase().trim();
    };
    const getFechaBaja = (exp) => exp?.fecha_baja || exp?.fecha_ejecucion || exp?.fecha_solicitacion || null;
    const getFechaSolicitud = (exp) => exp?.fecha_solicitud || exp?.fecha_encargo || null;
    const getDateValue = (v) => {
      const d = dayjs(v);
      return d.isValid() ? d.valueOf() : null;
    };
    const compareByGetter = (getter, dir) => (a, b) => {
      const da = getDateValue(getter(a));
      const db = getDateValue(getter(b));
      if (da === null && db === null) return 0;
      if (da === null) return 1;
      if (db === null) return -1;
      return dir === "asc" ? da - db : db - da;
    };

    switch (sortOption) {
      case "az":
        list.sort((a, b) => getCaseTitleSortKey(a).localeCompare(getCaseTitleSortKey(b)));
        break;
      case "za":
        list.sort((a, b) => getCaseTitleSortKey(b).localeCompare(getCaseTitleSortKey(a)));
        break;
      case "baja_asc":
        list.sort(compareByGetter(getFechaBaja, "asc"));
        break;
      case "baja_desc":
        list.sort(compareByGetter(getFechaBaja, "desc"));
        break;
      case "solicitud_asc":
        list.sort(compareByGetter(getFechaSolicitud, "asc"));
        break;
      case "solicitud_desc":
        list.sort(compareByGetter(getFechaSolicitud, "desc"));
        break;
      default:
        break;
    }
    return list;
  }, [extraFiltered, sortOption]);

  const rowsPerPage = PAGE_SIZE || 20;
  const totalEncontrados = sortedFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalEncontrados / rowsPerPage));
  const rows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedFiltered.slice(start, start + rowsPerPage).map((exp) => mapRow(exp, modalidadMap, lugarMap));
  }, [sortedFiltered, currentPage, rowsPerPage, modalidadMap, lugarMap]);

  /* ── handlers ── */
  const handleCrearExpediente = () => {
    const estadoParam = selectedEstadoId !== ALL ? selectedEstadoId : null;
    const ciudadParam = selectedCiudadId !== ALL ? selectedCiudadId : null;
    navigate("/materias/laboral/desvinculaciones/crear/", {
      state: { estado: estadoParam, ciudad: ciudadParam },
    });
  };

  const handleNavigateDetail = (id) => {
    navigate(`/materias/laboral/desvinculaciones/${id}`, {
      state: { from: location.pathname + location.search },
    });
  };

  const toggleStatus = (key) => {
    setStatusFilter((prev) => (prev === key ? "todos" : key));
    setCurrentPage(1);
  };

  const handleExportNow = () => {
    const list = Array.isArray(sortedFiltered) ? sortedFiltered : [];
    const now = new Date();
    const y = now.getFullYear();
    const periodFrom = `${y}-01-01`;
    const periodTo = `${y}-12-31`;
    const ciudadesLabel = ciudadSeleccionada?.nombre || "Todas";
    const nombreBusqueda = search || "Todos";
    const html = previewDesvinculacionesReportPdf({ items: list, periodFrom, periodTo, ciudadesLabel, nombreBusqueda });
    setExportPreviewHtml(html);
    setExportPreviewOpen(true);
  };

  /* ─────────────────────────────────────── render ───────────────────── */
  return (
    <ConfigProvider locale={esES}>
    <HappyProvider>
    <div>
      <Modal
        open={exportPreviewOpen}
        onCancel={() => setExportPreviewOpen(false)}
        centered
        width={1140}
        title="Previsualización"
        footer={[
          <Button key="close" onClick={() => setExportPreviewOpen(false)}>
            Cerrar
          </Button>,
          <Button
            key="export"
            type="primary"
            onClick={() => {
              const list = Array.isArray(sortedFiltered) ? sortedFiltered : [];
              const now = new Date();
              const y = now.getFullYear();
              const ciudadesLabel = ciudadSeleccionada?.nombre || "Todas";
              const nombreBusqueda = search || "Todos";
              printDesvinculacionesReportPdf({ items: list, periodFrom: `${y}-01-01`, periodTo: `${y}-12-31`, ciudadesLabel, nombreBusqueda });
            }}
          >
            Exportar PDF
          </Button>,
        ]}
      >
        <div style={{ height: "72vh", border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <iframe title="preview-desvinculaciones" style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} srcDoc={exportPreviewHtml} />
        </div>
      </Modal>

      <main className="trib-main">
        <div className="trib-wrap">

          {/* ── Filter Panel ── */}
          <div className="laboral-filters-panel">
            <div className="laboral-panel-header">
              <h1 className="laboral-panel-title">Desvinculaciones · Materia laboral</h1>
              {estadoSeleccionado && ciudadSeleccionada ? (
                <p className="laboral-panel-subtitle">
                  Expedientes de desvinculaciones en <b>{estadoSeleccionado.nombre}</b> /{" "}
                  <b>{ciudadSeleccionada.nombre}</b>.
                </p>
              ) : estadoSeleccionado ? (
                <p className="laboral-panel-subtitle">
                  Expedientes de desvinculaciones en <b>{estadoSeleccionado.nombre}</b> · todas las ciudades.
                </p>
              ) : (
                <p className="laboral-panel-subtitle">
                  Todos los expedientes de desvinculaciones.
                </p>
              )}
            </div>

            {/* Fila 1: Estado / Ciudad / Empresa */}
            <div className="laboral-filter-row-1">
              <div className="laboral-field">
                <div className="laboral-field-label">ESTADO</div>
                <Select
                  className="laboral-control"
                  showSearch
                  placeholder="Todos los estados"
                  options={estadosOptionsFinal}
                  value={selectedEstadoId}
                  onChange={handleChangeEstado}
                  optionFilterProp="label"
                />
              </div>

              <div className="laboral-field">
                <div className="laboral-field-label">CIUDAD</div>
                <Select
                  className="laboral-control"
                  showSearch
                  placeholder="Todas las ciudades"
                  options={ciudadesOptionsFinal}
                  value={selectedCiudadId}
                  onChange={handleChangeCiudad}
                  optionFilterProp="label"
                />
              </div>

              <div className="laboral-field">
                <div className="laboral-field-label">EMPRESA</div>
                <Select
                  className="laboral-control"
                  showSearch
                  placeholder="Todas las empresas"
                  value={empresaFilter}
                  onChange={(value) => { setEmpresaFilter(value ?? null); setCurrentPage(1); }}
                  options={empresasFinalOptions}
                  optionFilterProp="label"
                />
              </div>
            </div>

            {/* Fila 2: Buscador (flex-grow) + Limpiar */}
            <div className="laboral-filter-row-2">
              <div className="laboral-field laboral-field--search">
                <div className="laboral-field-label">BUSCADOR</div>
                <Input
                  className="laboral-search-modern__input"
                  prefix={<SearchOutlined className="laboral-search-modern__icon" />}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  placeholder="Buscar por expediente o trabajador..."
                />
              </div>

              <div className="laboral-field laboral-field--actions">
                <div className="laboral-field-label">&nbsp;</div>
                <div className="laboral-actions">
                  <Button
                    onClick={() => {
                      setSelectedEstadoId(ALL);
                      setSelectedCiudadId(ALL);
                      setSearch("");
                      setEmpresaFilter(null);
                      setStatusFilter("todos");
                      setModalidadFilter(null);
                      setResultadoFilter(null);
                      setCurrentPage(1);
                    }}
                    className="laboral-btn-clean"
                  >
                    Limpiar
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleCrearExpediente}
                    className="laboral-btn-create"
                    icon={
                      <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                        <path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z" />
                      </svg>
                    }
                  >
                    Crear Desvinculación
                  </Button>
                </div>
              </div>
            </div>

            {/* Fila 3: Modalidad de baja / Resultado */}
            <Row gutter={[16, 14]} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <div className="laboral-field">
                  <div className="laboral-field-label">MODALIDAD DE BAJA</div>
                  <Select
                    placeholder="Todas las modalidades"
                    value={modalidadFilter}
                    onChange={(value) => { setModalidadFilter(value ?? null); setCurrentPage(1); }}
                    options={MODALIDAD_FILTER_OPTIONS}
                    allowClear
                    className="laboral-control"
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="laboral-field">
                  <div className="laboral-field-label">RESULTADO</div>
                  <Select
                    placeholder="Todos los resultados"
                    value={resultadoFilter}
                    onChange={(value) => { setResultadoFilter(value ?? null); setCurrentPage(1); }}
                    options={RESULTADO_FILTER_OPTIONS}
                    allowClear
                    className="laboral-control"
                  />
                </div>
              </Col>
            </Row>
          </div>

          {/* ── KPI Cards ── */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <KpiCardDesv
                icon="fa-circle"
                iconBg="gray"
                value={String(statusCounts?.registradas ?? 0)}
                label="Registradas"
                active={statusFilter === "registradas"}
                onClick={() => toggleStatus("registradas")}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardDesv
                icon="fa-hourglass-half"
                iconBg="yellow"
                value={String(statusCounts?.enProceso ?? 0)}
                label="En proceso"
                active={statusFilter === "enProceso"}
                onClick={() => toggleStatus("enProceso")}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardDesv
                icon="fa-circle-check"
                iconBg="green"
                value={String(statusCounts?.solucionadas ?? 0)}
                label="Solucionadas"
                active={statusFilter === "solucionadas"}
                onClick={() => toggleStatus("solucionadas")}
              />
            </Col>
            <Col xs={24} md={6}>
              <KpiCardDesv
                icon="fa-triangle-exclamation"
                iconBg="red"
                value={String(statusCounts?.esperaCitatorio ?? 0)}
                label="En espera de citatorio"
                active={statusFilter === "esperaCitatorio"}
                onClick={() => toggleStatus("esperaCitatorio")}
              />
            </Col>
          </Row>

          {/* ── Main section ── */}
          <div className="viewWrap">
            <div className="viewWrap__header">
              <div className="viewWrap__title">
                <h2 className="sectionTitle">Expedientes ({totalEncontrados})</h2>
                <div className="sectionMeta">{rows.length} mostrados</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
                <Select
                  showSearch={false}
                  allowClear
                  placeholder="Ordenar por"
                  value={sortOption === "default" ? undefined : sortOption}
                  onChange={(v) => { setSortOption(v ?? "default"); setCurrentPage(1); }}
                  style={{ width: 160 }}
                  popupMatchSelectWidth={320}
                  className="laboral-control"
                  options={[
                    { label: "Nombre (A-Z)", value: "az" },
                    { label: "Nombre (Z-A)", value: "za" },
                    { label: "Fecha de baja · más próxima", value: "baja_asc" },
                    { label: "Fecha de baja · más antigua", value: "baja_desc" },
                    { label: "Fecha de solicitud · más próxima", value: "solicitud_asc" },
                    { label: "Fecha de solicitud · más antigua", value: "solicitud_desc" },
                  ]}
                />
                <Button
                  className="laboral-btn-create"
                  icon={<i className="fa-solid fa-calendar-xmark" />}
                  onClick={() => toggleStatus("vencidos")}
                  style={{
                    borderColor: "#ff4d4f",
                    color: statusFilter === "vencidos" ? "#fff" : "#ff4d4f",
                    background: statusFilter === "vencidos" ? "#ff4d4f" : "transparent",
                  }}
                >
                  Vencidos ({statusCounts?.vencidos ?? 0})
                </Button>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    className: "laboral-export-menu",
                    items: [
                      {
                        key: "export",
                        label: (
                          <ExportMenuRow
                            viewBox="0 0 384 512"
                            path="M88 304H80V256H88C101.3 256 112 266.7 112 280C112 293.3 101.3 304 88 304zM192 256H200C208.8 256 216 263.2 216 272V336C216 344.8 208.8 352 200 352H192V256zM224 0V128C224 145.7 238.3 160 256 160H384V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64C0 28.65 28.65 0 64 0H224zM64 224C55.16 224 48 231.2 48 240V368C48 376.8 55.16 384 64 384C72.84 384 80 376.8 80 368V336H88C118.9 336 144 310.9 144 280C144 249.1 118.9 224 88 224H64zM160 368C160 376.8 167.2 384 176 384H200C226.5 384 248 362.5 248 336V272C248 245.5 226.5 224 200 224H176C167.2 224 160 231.2 160 240V368zM288 224C279.2 224 272 231.2 272 240V368C272 376.8 279.2 384 288 384C296.8 384 304 376.8 304 368V320H336C344.8 320 352 312.8 352 304C352 295.2 344.8 288 336 288H304V256H336C344.8 256 352 248.8 352 240C352 231.2 344.8 224 336 224H288zM256 0L384 128H256V0z"
                            tone="primary"
                            title="Exportar PDF"
                            subtitle="Genera un reporte de los expedientes filtrados"
                          />
                        ),
                        onClick: handleExportNow,
                      },
                      { type: "divider" },
                      {
                        key: "importExcel",
                        label: (
                          <ExportMenuRow
                            viewBox="0 0 384 512"
                            path="M224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272.1 264.4L224 344l48.99 79.61C279.6 434.3 271.9 448 259.4 448h-26.43c-5.557 0-10.71-2.883-13.63-7.617L192 396l-27.31 44.38C161.8 445.1 156.6 448 151.1 448H124.6c-12.52 0-20.19-13.73-13.63-24.39L160 344L111 264.4C104.4 253.7 112.1 240 124.6 240h26.43c5.557 0 10.71 2.883 13.63 7.613L192 292l27.31-44.39C222.2 242.9 227.4 240 232.9 240h26.43C271.9 240 279.6 253.7 272.1 264.4zM256 0v128h128L256 0z"
                            tone="green"
                            title="Importar Excel"
                            subtitle="Carga masiva desde una plantilla"
                          />
                        ),
                        onClick: () => setImportModalOpen(true),
                      },
                    ],
                  }}
                >
                  <Button size="large" className="laboral-btn-create">
                    <svg className="me-1" viewBox="0 0 256 512" width="7" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                      <path d="M249.6 392.3l-104 112c-9.094 9.781-26.09 9.781-35.19 0l-103.1-112c-6.484-6.984-8.219-17.17-4.406-25.92S14.45 352 24 352H80V160H24C14.45 160 5.812 154.3 1.999 145.6C-1.813 136.8-.0781 126.7 6.406 119.7l104-112c9.094-9.781 26.09-9.781 35.19 0l104 112c6.484 6.984 8.219 17.17 4.406 25.92C250.2 154.3 241.5 160 232 160H176v192h56c9.547 0 18.19 5.656 22 14.41S256.1 385.3 249.6 392.3z" />
                    </svg>
                    Exportar / Importar
                  </Button>
                </Dropdown>
                <ImportarExcelModal
                  open={importModalOpen}
                  onClose={() => setImportModalOpen(false)}
                  onSuccess={() => dispatch(actionDesvinculacionesCards(filtrosBusqueda))}
                  endpoint="desvinculaciones/importar-excel"
                  context={{}}
                />
                <Segmented
                  className="laboral-segmented"
                  value={view}
                  onChange={setView}
                  options={[
                    {
                      value: "cards",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 320 512" width="8" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M88 352C110.1 352 128 369.9 128 392V440C128 462.1 110.1 480 88 480H40C17.91 480 0 462.1 0 440V392C0 369.9 17.91 352 40 352H88zM280 352C302.1 352 320 369.9 320 392V440C320 462.1 302.1 480 280 480H232C209.9 480 192 462.1 192 440V392C192 369.9 209.9 352 232 352H280zM40 320C17.91 320 0 302.1 0 280V232C0 209.9 17.91 192 40 192H88C110.1 192 128 209.9 128 232V280C128 302.1 110.1 320 88 320H40zM280 192C302.1 192 320 209.9 320 232V280C320 302.1 302.1 320 280 320H232C209.9 320 192 302.1 192 280V232C192 209.9 209.9 192 232 192H280zM40 160C17.91 160 0 142.1 0 120V72C0 49.91 17.91 32 40 32H88C110.1 32 128 49.91 128 72V120C128 142.1 110.1 160 88 160H40zM280 32C302.1 32 320 49.91 320 72V120C320 142.1 302.1 160 280 160H232C209.9 160 192 142.1 192 120V72C192 49.91 209.9 32 232 32H280z" />
                          </svg>
                          Tarjetas
                        </span>
                      ),
                    },
                    {
                      value: "table",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M448 32C483.3 32 512 60.65 512 96V416C512 451.3 483.3 480 448 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H448zM224 256V160H64V256H224zM64 320V416H224V320H64zM288 416H448V320H288V416zM448 256V160H288V256H448z" />
                          </svg>
                          Tabla
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {view === "table" ? (
              <TableView pageRows={rows} total={totalEncontrados} totalPages={totalPages} page={currentPage} setPage={setCurrentPage} onNavigate={handleNavigateDetail} />
            ) : (
              <CardsView rows={rows} total={totalEncontrados} totalPages={totalPages} page={currentPage} setPage={setCurrentPage} onNavigate={handleNavigateDetail} />
            )}
          </div>

        </div>
      </main>
    </div>
    </HappyProvider>
    </ConfigProvider>
  );
}
